/** The resend-code button and its cooldown.
 *
 * The decision half — how long is left, and is it up yet — is `resend.ts`, pure
 * over an injected `now` and unit-tested without timers. This is the DOM half:
 * the button, its counting-down label, and the ticker, whose lifetime must not
 * outlive the code step (leave via "Use a different email address" 10 seconds in
 * and a stray interval would keep relabelling a hidden button). Those were three
 * flow-level functions plus a mutable timer handle that the step machine had to
 * remember to clear; here the thing that starts the ticker owns stopping it.
 */

import { h } from "../lib/jsx";
import { createResendCooldown } from "./resend";

const LABEL = "Resend code";

export interface ResendButton {
  el: HTMLButtonElement;
  /** Begin (or begin again) the cooldown, and tick the label down. */
  start(): void;
  /** Stop ticking. Idempotent, so the flow can call it whenever the code step
   * is left without knowing whether the cooldown had elapsed. */
  stop(): void;
}

export function createResendButton(opts: {
  seconds: number;
  /** A click once the cooldown has elapsed. Clicks before that do nothing, so
   * callers never re-check readiness. */
  onResend(): void;
}): ResendButton {
  const cooldown = createResendCooldown(opts.seconds);
  let timer: ReturnType<typeof setInterval> | null = null;

  const el = (
    <button class="otp-btn otp-btn-link" type="button" onClick={onClick}>
      {LABEL}
    </button>
  ) as HTMLButtonElement;

  function onClick(): void {
    if (cooldown.ready(Date.now())) opts.onResend();
  }

  function stop(): void {
    if (timer) {
      clearInterval(timer);
      timer = null;
    }
  }

  function tick(): void {
    const now = Date.now();
    if (cooldown.ready(now)) {
      el.disabled = false;
      el.textContent = LABEL;
      stop();
    } else {
      el.disabled = true;
      el.textContent = `${LABEL} (${cooldown.remaining(now)}s)`;
    }
  }

  return {
    el,
    start() {
      cooldown.start(Date.now());
      tick();
      stop();
      timer = setInterval(tick, 1000);
    },
    stop,
  };
}
